const STATUS_PRIORITY = {
  inbox: 0,
  needs_review: 1,
  archived: 2,
};

const CONTEXT_TYPES = new Set(["research_brief"]);

export const INBOX_TABS = [
  { id: "all", label: "All intake" },
  { id: "inbox", label: "New" },
  { id: "needs_review", label: "Ready to review" },
  { id: "archived", label: "Archived" },
];

export const INBOX_LANES = [
  { id: "all", label: "Total intake", hint: "Everything received from outside tools" },
  { id: "inbox", label: "New intake", hint: "Raw items waiting for triage" },
  { id: "needs_review", label: "Ready to review", hint: "Drafts that already have shape" },
  { id: "archived", label: "Archived", hint: "Handled or dismissed items" },
];

function normalizeDate(value) {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
}

function getSearchText(event = {}) {
  return [
    event.title,
    event.source,
    event.preview,
    event.platform,
    event.asset_type,
    event.payload?.origin,
  ].filter(Boolean).join(" ").toLowerCase();
}

export function getInboxEventAction(event = {}) {
  if (event.status === "archived") {
    return {
      label: "Archived",
      tone: "archived",
      hint: "Already removed from the active intake queue.",
    };
  }

  if (CONTEXT_TYPES.has(event.asset_type)) {
    return {
      label: "Use as context",
      tone: "context",
      hint: "Keep this as source material for a future run.",
    };
  }

  if (event.status === "needs_review") {
    return {
      label: "Review draft",
      tone: "review",
      hint: "Open this draft and decide whether to keep, revise, or schedule.",
    };
  }

  return {
    label: "Create draft",
    tone: "draft",
    hint: "Turn this intake item into a reviewable content asset.",
  };
}

export function filterInboxEvents(events = [], filters = {}) {
  const tab = filters.tab || "all";
  const source = filters.source || "all";
  const query = (filters.query || "").trim().toLowerCase();

  return events
    .filter((event) => tab === "all" || event.status === tab)
    .filter((event) => source === "all" || event.source === source)
    .filter((event) => !query || getSearchText(event).includes(query))
    .sort((a, b) => {
      const priority = (STATUS_PRIORITY[a.status] ?? 3) - (STATUS_PRIORITY[b.status] ?? 3);
      if (priority !== 0) return priority;
      return normalizeDate(b.received_at) - normalizeDate(a.received_at);
    });
}

export function buildInboxWorkspace({ events = [], filters = {} } = {}) {
  const sourceCounts = events.reduce((acc, event) => {
    const source = event.source || "unknown";
    acc.set(source, (acc.get(source) || 0) + 1);
    return acc;
  }, new Map());

  const summary = {
    total: events.length,
    active: events.filter((event) => event.status !== "archived").length,
    inbox: events.filter((event) => event.status === "inbox").length,
    needsReview: events.filter((event) => event.status === "needs_review").length,
    archived: events.filter((event) => event.status === "archived").length,
    sources: sourceCounts.size,
  };

  const sourceBreakdown = Array.from(sourceCounts.entries())
    .map(([id, count]) => ({ id, count }))
    .sort((a, b) => b.count - a.count || a.id.localeCompare(b.id));

  const laneCount = (id) => (id === "all" ? summary.total : events.filter((event) => event.status === id).length);
  const lanes = INBOX_LANES.map((lane) => ({ ...lane, count: laneCount(lane.id) }));
  // Counts live on the tabs (stats-as-filters) — no separate stat-card row.
  const tabs = INBOX_TABS.map((tab) => ({ ...tab, count: laneCount(tab.id) }));

  const filtered = filterInboxEvents(events, filters).map((event) => ({
    ...event,
    inboxAction: getInboxEventAction(event),
  }));

  return {
    summary,
    sourceBreakdown,
    tabs,
    lanes,
    filtered,
  };
}
